/* eslint-disable import/extensions */
/* eslint-disable import/no-extraneous-dependencies */
import { useContext, useEffect, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import {
  QTypography,
  QButton,
  QContainer,
} from '@kw/quokka-ui';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import type { ListResponse } from '@kw/rules-service/dist/src/types/ListResponse';
import type { ViolationCounts } from '@kw/rules-service/dist/src/policy/dtos/CurrentTriggeredDevicesApplications.dto';
import { type PolicyAction, getActions, getMdmActions, PolicyActionGroup, PolicyRule, RuleEntity, PolicyEntity } from './getData';
import { PolicyListCard } from './PolicyListCard';
import { PolicyStatusCard } from './PolicyStatusCard';
import { PolicySelectedListCard } from './PolicySelectedListCard';
import { PolicyTab } from './enum/PolicyTab.enum';
import { PolicyStatisticsCard } from './PolicyStatisticsCard';
import { PolicyCreationCard } from './PolicyCreationCard';
import { PolicyProfileMode } from './enum/PolicyProfileMode.enum';
import { sendHttpRequest } from '../../utils/network.service';
import { useLocalStorage } from '../../utils/useLocalStorage';
import { useFetchService } from '../../utils/fetchService';
import { PolicyInfoContext } from './PolicyInfoContext';
import { LaunchDarklyFlags } from '../../launch-darkly';
import { useOrg } from '../Organization/org-provider';
import { orgHasSetting } from '../Organization/org-utils';
import { PolicyRuleParametersLegend } from './enum/PolicyRuleParameters.enum';

export function PolicyProfilePage({ mode }: { mode: PolicyProfileMode }) {
  const {
    policyName,
    policyEnabled,
    policyDescription,
    policyFormErrors,
    policyRuleParameters,
    setPolicyName,
    setPolicyEnabled,
    setPolicyDescription,
    setPolicyCreatedOn,
    setPolicyFormErrors,
    setPolicyRuleParameters,
  } = useContext(PolicyInfoContext);

  const navigate = useNavigate();
  const { policyId } = useParams();

  const [accessToken = ''] = useLocalStorage('accessToken', '');
  const { orgData } = useOrg();
  const isMdmMode = orgHasSetting(orgData, 'mdm-integration');
  const [tab, setTab] = useState(PolicyTab.RULES);
  const [mdmActions] = useState(getMdmActions());
  const [baseActions] = useState(getActions());

  const [selectedRules, setSelectedRules] = useState([]);
  const [selectedActions, setSelectedActions] = useState([]);
  const [policyData, setPolicyData] = useState(null);
  const [modalOptions, setModalOptions] = useState({ visible: false, row: null, isEdit: false });

  const { enqueueSnackbar } = useSnackbar();

  const flags: LaunchDarklyFlags['flags'] = useFlags();

  let rulesPath = '/eval/rules/list';
  let currentTriggeredDevicesPath = `/eval/policies/current-triggered-devices-applications?ruleIds=${selectedRules
    .map(rule => rule.id)
    .join(',')}&threatLevel=${policyRuleParameters.maliciousThreshold > 0 ? policyRuleParameters.maliciousThreshold : null}`;

  let policiesPath = '/eval/policies/list';

  const { data: rulesData } = useFetchService<ListResponse<RuleEntity>>(rulesPath, accessToken);

  const { data: policyResponse } = useFetchService<ListResponse<PolicyEntity>>(policiesPath, accessToken);
  const { data: triggeredDevicesAndApps } = useFetchService<ViolationCounts>(
    currentTriggeredDevicesPath,
    accessToken,
    selectedRules.length > 0,
  );
  let actions;
  if (isMdmMode) {
    actions = mdmActions;
  } else {
    actions = baseActions;
  }

  useEffect(() => {
    if ([PolicyProfileMode.EDIT, PolicyProfileMode.VIEW].includes(mode)) {
      if (!policyResponse) {
        return;
      }
      const data = policyResponse.results.find((policy: PolicyEntity) => policy.id === policyId);

      setPolicyData(data);
      setSelectedRules(data.policyRules.map((policyRule: PolicyRule) => policyRule.rule));

      data.policyRules.map((policyRule: PolicyRule) => {
        const { name } = policyRule.rule;

        if (PolicyRuleParametersLegend[name]) {
          const parameter: { [key: string]: string } = {};
          const key = PolicyRuleParametersLegend[name].parameter;
          parameter[key] = policyRule.parameters?.[key];

          setPolicyRuleParameters({ ...policyRuleParameters, ...parameter });
        }
        return null;
      });

      const emailNotification = data.policyActions?.find(a => a.action.name === 'NOTIFICATION');
      if (emailNotification) {
        for (const action of actions) {
          for (const subAction of action.actions) {
            if (subAction.key === 'NOTIFICATION') {
              subAction.emails = data.notificationEmails;
            }
          }
        }
      }
      const filteredActions = actions.reduce((ret, val: PolicyActionGroup) => {
        const actionIncluded = val.actions.filter(action => data.remediations.includes(action.key));
        if (actionIncluded.length) {
          ret = [...ret, ...actionIncluded];
        }
        return ret;
      }, []);
      setSelectedActions(filteredActions);
      setPolicyName(data.name);
      setPolicyEnabled(data.enabled);
      setPolicyDescription(data.description);
      setPolicyCreatedOn(data.createdAt);
    } else {
      setPolicyName('');
      setPolicyDescription('');
      setPolicyEnabled(true);
      setPolicyCreatedOn(dayjs().format('MM/DD/YYYY'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, actions, policyId, setPolicyDescription, setPolicyEnabled, setPolicyName, setPolicyCreatedOn, policyResponse, isMdmMode]);

  const resetParameters = (id: string) => {
    const ruleWithParameter = selectedRules.find(rule => !!PolicyRuleParametersLegend[rule.name]);
    if (ruleWithParameter?.id === id) {
      const { parameter: key, initialValue } = PolicyRuleParametersLegend[ruleWithParameter.name];
      setPolicyRuleParameters({ ...policyRuleParameters, [key]: initialValue });
    }
  };

  const addRules = (item: RuleEntity) => {
    setSelectedRules([...selectedRules, item]);
    updatePolicyRuleError();
  };

  const addActions = (item: PolicyAction) => {
    const updatedActions = [...selectedActions];
    const existingIndex = updatedActions.findIndex(action => action.id === item.id);
    if (existingIndex > -1) {
      updatedActions.splice(existingIndex, 1, item);
    } else {
      updatedActions.push(item);
    }
    setSelectedActions(updatedActions);
  };

  const removeRules = (id: string) => {
    setSelectedRules(selectedRules.filter(rule => rule.id !== id));
    updatePolicyRuleError();
    resetParameters(id);
  };

  const removeActions = (id: number) => {
    const existingAction = selectedActions.find(action => action.id === id);
    if (existingAction.emails?.length > 0) {
      existingAction.emails = [];
    }
    setSelectedActions(selectedActions.filter(action => action.id !== id));
  };

  const validatePolicy = () => {
    setPolicyFormErrors({ name: !policyName.length, rules: !selectedRules.length, validated: true });
  };

  const updatePolicyRuleError = () => {
    const { rules, validated } = policyFormErrors;
    const errorStatus = rules && validated ? !!selectedRules.length : validated ? !selectedRules.length : rules;
    setPolicyFormErrors({ rules: errorStatus, ...policyFormErrors });
  };

  useEffect(() => {
    // only runs after initial error state is displayed (user tries to save without name || rules)
    if (policyFormErrors.validated) {
      if (policyFormErrors.name || policyFormErrors.rules) {
        validatePolicy();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policyName, selectedRules]);

  const handlePolicySubmit = async ({
    savedPolicyId = '',
    savedPolicyName,
    savedRules,
    savedActions,
    savedPolicyDescription,
    savedPolicyEnabled,
  }: {
    savedPolicyId?: string;
    savedPolicyName: string;
    savedRules: RuleEntity[];
    savedActions: PolicyAction[];
    savedPolicyDescription: string;
    savedPolicyEnabled: boolean;
  }) => {
    validatePolicy();

    if (savedPolicyName.length && savedRules.length) {
      let path = `${process.env.EVAL_SERVICE_URL}/eval/policies/${mode === PolicyProfileMode.CREATE ? 'create' : 'update'}`;

      const policyRules = savedRules.map((rule: RuleEntity) => {
        // set parameters for rules that require them
        if (PolicyRuleParametersLegend[rule.name]) {
          const parameters: { [key: string]: string } = {};
          const key = PolicyRuleParametersLegend[rule.name].parameter;
          parameters[key] = policyRuleParameters[key];
          return {
            ruleId: rule.id,
            parameters,
          };
        }
        return { ruleId: rule.id };
      });

      const body = {
        id: savedPolicyId,
        name: savedPolicyName,
        description: savedPolicyDescription,
        enabled: savedPolicyEnabled,
        remediations: savedActions.map((action: PolicyAction) => action.key),
        policyRules,
        notificationEmails: savedActions?.find(action => action.key === 'NOTIFICATION')?.emails || [],
      };

      if (!savedPolicyId.length) {
        delete body.id;
      }

      await sendHttpRequest({
        path,
        method: 'POST',
        body,
        successMessage: 'Successfully saved policy',
        errorMessage: 'Error saving policy',
        enqueueSnackbar,
      });

      navigate('/policies');
    }
  };

  const StatusCard = () => {
    return (
      <StyledStack>
        <QTypography variant="h5Medium">Status Update</QTypography>
        <StyledStatusCards>
          <PolicyStatusCard title="Devices" value={triggeredDevicesAndApps?.numDevicesTriggered || '-'} />
          <PolicyStatusCard title="Applications" value={triggeredDevicesAndApps?.numApplicationsTriggering || '-'} />
        </StyledStatusCards>
      </StyledStack>
    );
  };

  const handleEdit = (action: PolicyAction) => {
    setModalOptions({ visible: true, row: action, isEdit: true });
  };

  return (
    <QContainer maxWidth={false}>
      <StyledSplitPanel>
        <StyledBackSection>
          <QButton variant="light" startIcon={<ArrowBack />} onClick={() => navigate('/policies')}>
            Back to Policies
          </QButton>
        </StyledBackSection>
        <StyledActionSection>
          {mode === PolicyProfileMode.EDIT ? (
            <>
              <QButton variant="basic" onClick={() => navigate(`/policies/view/${policyId}`)}>
                Cancel
              </QButton>
              <QButton
                variant="filled"
                onClick={() =>
                  handlePolicySubmit({
                    savedPolicyId: policyId,
                    savedPolicyName: policyName,
                    savedPolicyDescription: policyDescription,
                    savedPolicyEnabled: policyEnabled,
                    savedRules: selectedRules,
                    savedActions: selectedActions,
                  })
                }
              >
                Save
              </QButton>
            </>
          ) : mode === PolicyProfileMode.CREATE ? (
            <>
              <QButton variant="basic" onClick={() => navigate('/policies')}>
                Cancel
              </QButton>
              <QButton
                variant="filled"
                onClick={() =>
                  handlePolicySubmit({
                    savedPolicyName: policyName,
                    savedPolicyDescription: policyDescription,
                    savedPolicyEnabled: policyEnabled,
                    savedRules: selectedRules,
                    savedActions: selectedActions,
                  })
                }
              >
                Create
              </QButton>
            </>
          ) : (
            <QButton variant="filled" onClick={() => navigate(`/policies/edit/${policyId}`)}>
              Edit
            </QButton>
          )}
        </StyledActionSection>

        {[PolicyProfileMode.EDIT, PolicyProfileMode.VIEW].includes(mode) && (
          <>
            {policyId ? (
              <StyledStatisticsSection>
                <PolicyStatisticsCard policyId={policyId} />
              </StyledStatisticsSection>
            ) : null}
          </>
        )}

        <StyledDetailsSection>
          <QTypography variant="h4Medium">Policy Details</QTypography>

          {[PolicyProfileMode.EDIT, PolicyProfileMode.VIEW].includes(mode) && (
            <Box display="flex" flexDirection="column">
              <QTypography variant="bodyLight">Created On: {dayjs(policyData?.createdAt).format('MM/DD/YYYY')}</QTypography>
              <QTypography variant="bodyLight">Last Modified: {dayjs(policyData?.updatedAt).format('MM/DD/YYYY')}</QTypography>
            </Box>
          )}
        </StyledDetailsSection>
        <StyledProfileSection>
          <PolicyCreationCard disabled={![PolicyProfileMode.EDIT, PolicyProfileMode.CREATE].includes(mode)} />
        </StyledProfileSection>
        {mode === PolicyProfileMode.CREATE ? (
          <StyledStatisticsSection>
            <StatusCard />
          </StyledStatisticsSection>
        ) : (
          <StyledStatusSection>
            <StatusCard />
          </StyledStatusSection>
        )}
        <StyledRuleSection>
          <StyledStack>
            <QTypography variant="h5Medium">Rules</QTypography>
            <PolicySelectedListCard
              selectedList={selectedRules}
              originalList={rulesData ? rulesData.results : []}
              title="rules"
              removeAction={removeRules}
              isHighlight={tab === PolicyTab.RULES}
              mode={mode}
            />
          </StyledStack>
        </StyledRuleSection>
        <StyledActionsSection>
          <StyledStack>
            <QTypography variant="h5Medium">Actions (Optional)</QTypography>
            <PolicySelectedListCard
              selectedList={selectedActions}
              originalList={actions}
              title="actions"
              removeAction={removeActions}
              isHighlight={tab === PolicyTab.ACTIONS}
              mode={mode}
              handleEdit={handleEdit}
            />
          </StyledStack>
        </StyledActionsSection>
        <StyledPoliciesSection>
          <PolicyListCard
            tab={tab}
            setTab={setTab}
            rules={rulesData ? rulesData.results : []}
            actions={actions}
            selectedRules={selectedRules}
            selectedActions={selectedActions}
            handleAddingRules={addRules}
            handleAddingActions={addActions}
            mode={mode}
            modalOptions={modalOptions}
            setModalOptions={setModalOptions}
          />
        </StyledPoliciesSection>
      </StyledSplitPanel>
    </QContainer>
  );
}

const StyledContainer = styled(QContainer)`
  padding: 1% 3%;
` as typeof QContainer;

const StyledStack = styled('div')({ display: 'flex', flexDirection: 'column', gap: '10px' });

const StyledSplitPanel = styled('div')({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gridTemplateRows: 'auto auto auto auto auto auto',
  gridTemplateAreas: `
  'back action'
  'details details'
  'profile statistics' 
  'status statistics'
  'rule policies'
  'actions policies'`,
  gap: '20px 44px',
});

const StyledBackSection = styled('div')({
  gridArea: 'back',
});
const StyledActionSection = styled('div')({
  gridArea: 'action',
  display: 'inline-flex',
  justifyContent: 'flex-end',
});

const StyledProfileSection = styled('div')({
  gridArea: 'profile',
});

const StyledStatusSection = styled('div')({
  gridArea: 'status',
});

const StyledRuleSection = styled('div')({
  gridArea: 'rule',
});

const StyledActionsSection = styled('div')({
  gridArea: 'actions',
});

const StyledPoliciesSection = styled('div')({
  gridArea: 'policies',
  top: 0,
  position: 'sticky',
  alignSelf: 'start',
});

const StyledStatisticsSection = styled('div')({
  gridArea: 'statistics',
  paddingBottom: '60px',
});

const StyledDetailsSection = styled('div')({
  gridArea: 'details',
  display: 'flex',
  justifyContent: 'space-between',
});

const StyledStatusCards = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'auto auto',
  gap: '24px',
});
